import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@optools/components/ui/select';
import {
  MediaFeedType,
  MediaType,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { X } from 'lucide-react';

import { BroadcastFilter, BroadcastFilterKey } from '../../utils/broadcasts';

export interface BroadcastFiltersProps {
  filters: Partial<BroadcastFilter>;
  onClearFilter: (keys: BroadcastFilterKey[]) => void;
  onSetFilter: (key: BroadcastFilterKey, value: BroadcastFilter[BroadcastFilterKey]) => void;
}

export function BroadcastFilters({ filters, onClearFilter, onSetFilter }: BroadcastFiltersProps) {
  const setFilter = (key: BroadcastFilterKey, value: BroadcastFilter[BroadcastFilterKey]) => {
    onSetFilter(key, value);
  };

  const handleClearFilter = (keys: BroadcastFilterKey | BroadcastFilterKey[]) => {
    onClearFilter(Array.isArray(keys) ? keys : [keys]);
  };

  return (
    <div className="mb-4">
      <div className="w-full overflow-x-auto">
        <div className="flex w-max items-center gap-2">
          <Select
            value={'availableForStreaming' in filters ? String(filters.availableForStreaming) : ''}
            onValueChange={(value) => setFilter('availableForStreaming', value === 'true')}>
            <SelectTrigger aria-label="Available for Streaming">
              Available for Streaming
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={'true'}>Yes</SelectItem>
              <SelectItem value={'false'}>No</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.feedType ?? ''}
            onValueChange={(value) => setFilter('feedType', value)}>
            <SelectTrigger aria-label="Feed Type">Feed Type</SelectTrigger>
            <SelectContent>
              <SelectItem value={MediaFeedType.HOME}>Home</SelectItem>
              <SelectItem value={MediaFeedType.AWAY}>Away</SelectItem>
              <SelectItem value={MediaFeedType.NATIONAL}>National</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.mediaType ?? ''}
            onValueChange={(value) => setFilter('mediaType', value)}>
            <SelectTrigger aria-label="Media Type">Media Type</SelectTrigger>
            <SelectContent>
              <SelectItem value={MediaType.VIDEO}>Video</SelectItem>
              <SelectItem value={MediaType.AUDIO}>Audio</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.language ?? ''}
            onValueChange={(value) => setFilter('language', value)}>
            <SelectTrigger aria-label="Language">Language</SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Spanish</SelectItem>
            </SelectContent>
          </Select>

          {Object.keys(filters).length > 0 && (
            <Button
              aria-label="Clear Filters"
              size="icon"
              variant="outline"
              onClick={() =>
                handleClearFilter(['availableForStreaming', 'feedType', 'mediaType', 'language'])
              }>
              <X />
            </Button>
          )}
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 overflow-x-auto">
        {'availableForStreaming' in filters ? (
          <Filter
            filterKey="availableForStreaming"
            label="Available for Streaming"
            value={filters.availableForStreaming ? 'Yes' : 'No'}
            onClearFilter={handleClearFilter}
          />
        ) : null}

        {'feedType' in filters ? (
          <Filter
            filterKey="feedType"
            label="Feed Type"
            value={filters.feedType}
            onClearFilter={handleClearFilter}
          />
        ) : null}

        {'mediaType' in filters ? (
          <Filter
            filterKey="mediaType"
            label="Media Type"
            value={filters.mediaType}
            onClearFilter={handleClearFilter}
          />
        ) : null}

        {'language' in filters ? (
          <Filter
            filterKey="language"
            label="Language"
            value={filters.language === 'en' ? 'English' : 'Spanish'}
            onClearFilter={handleClearFilter}
          />
        ) : null}
      </div>
    </div>
  );
}

const Filter = ({
  filterKey,
  label,
  onClearFilter,
  value,
}: {
  label: string;
  value: BroadcastFilter[BroadcastFilterKey] | undefined;
  filterKey: BroadcastFilterKey;
  onClearFilter: (key: BroadcastFilterKey) => void;
}) => {
  return (
    <button onClick={() => onClearFilter(filterKey)}>
      <Badge variant="secondary">
        <span className="text-primary/80">{label}:</span> {value} <X />
      </Badge>
    </button>
  );
};
